/**
 * RevenueBenchmarkPanel — Top TrustMRR matches per opportunity with country
 * alignment ✓/⚠️ indicator. The ⚠️ count IS the arbitrage signal.
 *
 * Anonymous rows (name missing) are honestly labeled so they don't masquerade
 * as strong comps. The score-rounding quirk (Haiku clusters at 0.72/0.75/0.85)
 * is shown verbatim — no fake decimal precision.
 */
import React from "react";
import { TrendingUp, TrendingDown, ExternalLink } from "lucide-react";
import CountryAlignmentIndicator from "./CountryAlignmentIndicator";

const FLAGS = { US: "🇺🇸", GB: "🇬🇧", IN: "🇮🇳", ES: "🇪🇸", FR: "🇫🇷", DE: "🇩🇪", CA: "🇨🇦", AU: "🇦🇺", PL: "🇵🇱" };

const fmtUsd = (n) => {
  if (!n && n !== 0) return "?";
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}k`;
  return `$${Math.round(n)}`;
};

const fmtPct = (n) => {
  if (!n && n !== 0) return null;
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(1)}%`;
};

const MatchCard = ({ match, topCountry }) => {
  const isAnonymous = !match.name || match.name.toLowerCase().includes("anonymous");
  const country = match.country || null;
  const mrr = match.revenue_mrr_usd ?? match.revenue_last_30_days_usd;
  const growth = match.growth_30d;
  const score = match.match_score ?? match.similarity ?? 0;

  return (
    <div
      data-testid={`match-card-${match.trustmrr_id || match.name || "anon"}`}
      className="p-3 border border-zinc-200 space-y-2 hover:border-zinc-300 transition-colors"
    >
      {/* Header line */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-lg leading-none">{FLAGS[country] || "🌍"}</span>
            <span
              className={`font-bold truncate ${
                isAnonymous ? "italic text-zinc-500" : "text-zinc-900"
              }`}
              title={match.name}
            >
              {isAnonymous ? "Anonymous TrustMRR row" : match.name}
            </span>
          </div>
          {match.category && (
            <p className="text-xs text-zinc-500 mt-0.5 truncate">{match.category}</p>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className="text-base font-mono font-bold text-zinc-900">{fmtUsd(mrr)}</div>
          {growth !== null && growth !== undefined && (
            <div
              className={`text-xs font-mono inline-flex items-center gap-0.5 ${
                growth > 0 ? "text-emerald-600" : "text-red-600"
              }`}
            >
              {growth > 0 ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
              {fmtPct(growth)}
            </div>
          )}
        </div>
      </div>

      {/* Reasoning / alignment row */}
      {match.reasoning && (
        <p className="text-xs text-zinc-600 leading-snug">{match.reasoning}</p>
      )}
      <div className="flex items-center justify-between gap-2 pt-1 border-t border-zinc-100">
        <CountryAlignmentIndicator matched_country={country} top_country={topCountry} />
        <div className="flex items-center gap-2 text-xs text-zinc-500 font-mono">
          <span title="Match score from Haiku judge (rounds to {.72, .75, .85})">
            match={score.toFixed(2)}
          </span>
          {match.profile_url && (
            <a
              href={match.profile_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#7C3AED] hover:underline inline-flex items-center gap-1"
              data-testid="match-source-link"
            >
              source <ExternalLink size={10} />
            </a>
          )}
        </div>
      </div>
      {isAnonymous && (
        <p className="text-[10px] text-zinc-400 italic">
          TrustMRR caches some startups without revealing names. Treat as a directional signal, not a named comp.
        </p>
      )}
    </div>
  );
};

const AlignmentSummary = ({ alignment }) => {
  if (!alignment || alignment.alignment_score === null || alignment.alignment_score === undefined) {
    return null;
  }
  const pct = Math.round((alignment.alignment_score || 0) * 100);
  const tone =
    pct >= 66 ? "bg-emerald-50 text-emerald-800 border-emerald-300" :
    pct >= 33 ? "bg-amber-50 text-amber-800 border-amber-300" :
                "bg-red-50 text-red-800 border-red-300";
  return (
    <div
      data-testid="alignment-summary"
      className={`text-xs p-2 border font-mono ${tone}`}
    >
      <strong>{alignment.matched_country_overlap}/{alignment.total_matches}</strong> matches operate in algorithm&apos;s #1 country
      {" — "}
      <em className="font-sans">
        {pct >= 66 ? "ranking validated" : pct >= 33 ? "partial validation" : "arbitrage signal: matches succeed elsewhere"}
      </em>
    </div>
  );
};

const RevenueBenchmarkPanel = ({ matches = [], alignment, topCountry }) => {
  if (!matches.length) {
    return (
      <section data-testid="revenue-benchmark-panel" className="space-y-2">
        <h3 className="text-sm font-black uppercase tracking-wider text-zinc-900">Revenue Benchmark</h3>
        <div className="p-3 border border-zinc-200 bg-zinc-50 text-sm text-zinc-500">
          No verified TrustMRR comps matched this opportunity above the similarity threshold.
        </div>
      </section>
    );
  }
  return (
    <section data-testid="revenue-benchmark-panel" className="space-y-3">
      <header>
        <h3 className="text-sm font-black uppercase tracking-wider text-zinc-900">
          Revenue Benchmark · TrustMRR Comps
        </h3>
        <p className="text-xs text-zinc-500">
          {matches.length} startup{matches.length === 1 ? "" : "s"} solving a similar problem with real revenue.
        </p>
      </header>
      <AlignmentSummary alignment={alignment} />
      <div className="space-y-2">
        {matches.map((m, i) => (
          <MatchCard
            key={`${m.trustmrr_id || m.name || "match"}-${i}`}
            match={m}
            topCountry={topCountry}
          />
        ))}
      </div>
    </section>
  );
};

export default RevenueBenchmarkPanel;
